import { useContext, useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { Button, FormControlLabel, FormLabel, Radio, RadioGroup, Typography } from '@mui/material'

import { AppContext } from '/providers/app-context'
import PageWrapper from '/components/page-wrapper'
import Menu from '/components/templates/template-nav-menu'
import HelpText from '/help/template-data-export'
import downloadTemplateData from '/components/make-passes/download-template-data'
import { getTemplateData } from '/providers/templates-api'

const TemplateDataExport = () => {
  const { templateId } = useParams()
  const navigate = useNavigate()
  const { apiSource, apiKey, loadingTemplates, templates, user } = useContext(AppContext)
  const [format, setFormat] = useState(user?.preferences?.data_format ?? 'csv')
  const [usage, setUsage] = useState('studio')
  const [templateData, setTemplateData] = useState(null)
  const template = useMemo(() => templates.find(item => item.template_id === templateId) ?? null, [templates, templateId])

  useEffect(() => {
    const loadTemplate = async () => {
      const result = await getTemplateData({ apiSource, apiKey, templateId })
      setTemplateData(result?.ok ? result.template : null)
    }

    if (templateId) loadTemplate()
  }, [apiKey, apiSource, templateId])

  if (!loadingTemplates && !template) return <Navigate to='/templates' replace />
  const changeUsage = event => {
    const nextUsage = event.target.value
    setUsage(nextUsage)
    if (nextUsage === 'api') setFormat('json')
  }

  const download = () => downloadTemplateData({
    template: templateData ?? template,
    format,
    includeMailmerge: usage === 'studio'
  })
  return <PageWrapper title={template ? `Download Data Template (${template.name})` : 'Download Data Template'} HelpText={HelpText} heading='Download data template' selectedTemplate={template} menu={<Menu template={template} />}>
    <div className='p-5 max-w-2xl flex flex-col gap-5'>
      <Typography variant='h4'>Download data template</Typography>
      <Typography>Download default pass-variable values for <strong>{template?.name}</strong>.</Typography>
      <div>
        <FormLabel id='template-usage-label'>How will you use this data template?</FormLabel>
        <RadioGroup aria-labelledby='template-usage-label' value={usage} onChange={changeUsage}>
          <FormControlLabel value='studio' control={<Radio />} label='To create passes by uploading data in Studio' />
          <FormControlLabel value='api' control={<Radio />} label='To create passes using the API makePass function' />
        </RadioGroup>
      </div>
      <div>
        <FormLabel id='export-format-label'>Format</FormLabel>
        <RadioGroup aria-labelledby='export-format-label' row value={format} onChange={event => setFormat(event.target.value)}>
          <FormControlLabel value='json' control={<Radio />} label='JSON' />
          {usage === 'studio' && <>
            <FormControlLabel value='csv' control={<Radio />} label='CSV' />
            <FormControlLabel value='xls' control={<Radio />} label='Excel (XLSX)' />
          </>}
        </RadioGroup>
      </div>
      <div className='flex gap-3'>
        <Button onClick={() => navigate(`/templates/${templateId}/details`)}>Cancel</Button>
        <Button variant='contained' onClick={download} disabled={!templateData}>Download data template</Button>
      </div>
    </div>
  </PageWrapper>
}

export default TemplateDataExport
